import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Upload, CheckCircle2, AlertCircle, Lock, User, Calendar, FileVideo, Send } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSPHRASE_STORAGE_KEY = "rushers_contribute_passphrase";

const UploadContribute = () => {
  const [step, setStep] = useState("auth"); // auth → choose → form → uploading → done
  const [passphrase, setPassphrase] = useState("");
  const [info, setInfo] = useState(null);
  const [authError, setAuthError] = useState("");

  const [uploadType, setUploadType] = useState(""); // "player" | "event"
  const [playerId, setPlayerId] = useState("");
  const [eventKey, setEventKey] = useState("");
  const [files, setFiles] = useState([]);
  const [contributorName, setContributorName] = useState("");
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);

  // ページタイトル設定
  React.useEffect(() => {
    document.title = "三送会動画素材投稿";
  }, []);

  // 自動ログイン: localStorage に保存された合言葉で自動認証
  useEffect(() => {
    const saved = localStorage.getItem(PASSPHRASE_STORAGE_KEY);
    if (!saved) return;
    setPassphrase(saved);
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/contribute/info`, { params: { passphrase: saved } });
        setInfo(r.data);
        setStep("choose");
      } catch (e) {
        // 古い合言葉が無効になっていたらクリア
        localStorage.removeItem(PASSPHRASE_STORAGE_KEY);
      }
    })();
  }, []);

  // 合言葉認証
  const onAuth = async () => {
    setAuthError("");
    try {
      const r = await axios.get(`${API_BASE}/contribute/info`, { params: { passphrase } });
      setInfo(r.data);
      // 認証成功時に合言葉を保存（リロード・ガイドページ往復で再入力不要に）
      localStorage.setItem(PASSPHRASE_STORAGE_KEY, passphrase);
      setStep("choose");
    } catch (e) {
      if (e.response?.status === 401) setAuthError("合言葉が違います");
      else setAuthError(e.response?.data?.detail || "接続エラー");
    }
  };

  const onSelectFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const totalSizeMb = files.reduce((s, f) => s + f.size, 0) / (1024 * 1024);
  const overSize = info && totalSizeMb > info.max_size_mb;

  const onSubmit = async () => {
    if (!files.length) return;
    if (uploadType === "player" && !playerId) return;
    if (uploadType === "event" && !eventKey) return;

    setStep("uploading");
    setResults([]);
    setErrors([]);

    let i = 0;
    for (const file of files) {
      i++;
      const fd = new FormData();
      fd.append("passphrase", passphrase);
      fd.append("upload_type", uploadType);
      if (playerId) fd.append("player_id", playerId);
      if (eventKey) fd.append("event_key", eventKey);
      fd.append("contributor_name", contributorName);
      fd.append("message", message);
      fd.append("file", file);

      try {
        const r = await axios.post(`${API_BASE}/contribute/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setProgress(Math.round(((i - 1 + pct / 100) / files.length) * 100));
          },
          timeout: 30 * 60 * 1000, // 30分
        });
        setResults((prev) => [...prev, { name: file.name, ...r.data }]);
      } catch (e) {
        // 保護者向けに技術的エラーを優しいメッセージに変換
        const status = e.response?.status;
        const raw = e.response?.data?.detail || e.message || "";
        let friendly = raw;
        const lower = String(raw).toLowerCase();
        if (status === 503 || lower.includes("invalid_grant") || lower.includes("token has been")
            || lower.includes("メンテナンス") || lower.includes("一時的")) {
          friendly = "現在システムが一時的に利用できません。管理者に連絡しているのでしばらくしてから再度お試しください。";
        } else if (status === 413 || lower.includes("too large") || lower.includes("大きすぎ")) {
          friendly = "ファイルサイズが大きすぎます。1ファイルあたり 2GB 以下でお願いします。";
        } else if (status === 401 || lower.includes("合言葉")) {
          friendly = "合言葉が違います。投稿を最初からやり直してください。";
        } else if (lower.includes("network") || lower.includes("timeout")) {
          friendly = "ネットワークが不安定なようです。Wi-Fi 接続を確認して再度お試しください。";
        } else if (lower.includes("driveアップロード失敗") || lower.includes("upload")) {
          friendly = "アップロードに失敗しました。時間をおいて再度お試しください。";
        }
        setErrors((prev) => [...prev, {
          name: file.name,
          error: friendly,
        }]);
      }
    }
    setStep("done");
  };

  const reset = () => {
    setStep("choose");
    setUploadType("");
    setPlayerId("");
    setEventKey("");
    setFiles([]);
    setContributorName("");
    setMessage("");
    setProgress(0);
    setResults([]);
    setErrors([]);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8" style={{background: "var(--bg-deep)"}} data-testid="upload-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl panel-elevated p-6 sm:p-10"
      >
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/rushers_logo.png"
            alt="Rushers"
            className="mx-auto mb-6"
            style={{maxWidth: "180px", height: "auto"}}
            data-testid="rushers-logo"
          />
          <h1 className="font-display text-3xl sm:text-4xl mb-2">三送会動画素材投稿</h1>
          <p className="text-sm mb-3" style={{color: "var(--text-secondary)"}}>
            選手・保護者・関係者の皆さま、ありがとうございます。<br/>
            写真や動画をお寄せください。
          </p>
          <a
            href="/guide"
            className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity font-medium"
            style={{background: "rgba(212,165,116,0.18)", border: "1.5px solid rgba(212,165,116,0.55)", color: "var(--gold)"}}
            data-testid="open-guide"
          >
            📖 撮影ガイド・お願い文を見る
          </a>
          <p className="text-xs mt-2" style={{color: "var(--text-muted)"}}>
            ※ はじめての方は、まずこちらをご一読ください
          </p>
        </div>

        {/* メンテナンス警告（管理者がDrive接続を更新するまで投稿不可） */}
        {info && info.service_available === false && step !== "auth" && (
          <div
            className="mb-6 p-4 rounded-lg flex items-start gap-3"
            style={{background: "rgba(198,106,74,0.18)", border: "1.5px solid rgba(198,106,74,0.5)"}}
            data-testid="maintenance-banner"
          >
            <AlertCircle size={20} style={{color: "#E07B5C"}} className="shrink-0 mt-0.5"/>
            <div className="text-sm">
              <div className="font-semibold mb-1" style={{color: "#FFD3C2"}}>
                現在、システムが一時的に利用できません
              </div>
              <div style={{color: "rgba(255,211,194,0.85)"}}>
                {info.service_message || "管理者に連絡しているのでしばらくしてから再度お試しください。"}
              </div>
            </div>
          </div>
        )}

        {/* Step: 合言葉 */}
        {step === "auth" && (
          <div data-testid="auth-step">
            <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>
              <Lock size={12} className="inline mr-1"/> 合言葉
            </label>
            <input
              type="password"
              className="cinema mb-3"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onAuth()}
              placeholder="チームから案内された合言葉を入力"
              autoFocus
              data-testid="passphrase-input"
            />
            {authError && (
              <p className="text-xs mb-3 flex items-center gap-1" style={{color: "var(--red-warn)"}} data-testid="auth-error">
                <AlertCircle size={12}/> {authError}
              </p>
            )}
            <button className="btn-gold w-full" onClick={onAuth} disabled={!passphrase} data-testid="auth-submit">
              続ける
            </button>
          </div>
        )}

        {/* Step: 種類選択 + フォーム */}
        {step === "choose" && info && (
          <div data-testid="choose-step">
            <p className="text-xs uppercase tracking-wider mb-3" style={{color: "var(--text-muted)"}}>動画の種類</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                className={`p-4 rounded text-left text-sm transition-all border ${uploadType === "player" ? "border-[var(--gold)] bg-[rgba(212,165,116,0.08)]" : ""}`}
                style={{borderColor: uploadType === "player" ? "var(--gold)" : "var(--border-strong)", color: uploadType === "player" ? "var(--gold)" : "var(--text-secondary)"}}
                onClick={() => setUploadType("player")}
                data-testid="type-player"
              >
                <User size={18} className="mb-2"/>
                <div className="font-medium mb-1">選手の動画</div>
                <div className="text-[11px] opacity-70">特定の3年生選手が映っている動画/写真</div>
              </button>
              <button
                className={`p-4 rounded text-left text-sm transition-all border ${uploadType === "event" ? "border-[var(--gold)] bg-[rgba(212,165,116,0.08)]" : ""}`}
                style={{borderColor: uploadType === "event" ? "var(--gold)" : "var(--border-strong)", color: uploadType === "event" ? "var(--gold)" : "var(--text-secondary)"}}
                onClick={() => setUploadType("event")}
                data-testid="type-event"
              >
                <Calendar size={18} className="mb-2"/>
                <div className="font-medium mb-1">チーム・イベント</div>
                <div className="text-[11px] opacity-70">試合、練習、応援、保護者の様子など</div>
              </button>
            </div>

            {uploadType === "player" && (
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>選手を選んでください</label>
                <select className="cinema" value={playerId} onChange={(e) => setPlayerId(e.target.value)} data-testid="player-select">
                  <option value="">— 選手を選択 —</option>
                  {info.players.map(p => (
                    <option key={p.id} value={p.id}>
                      #{p.jersey_number} {p.name} ({p.position})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {uploadType === "event" && (
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>いつ・何の動画？</label>
                <select className="cinema" value={eventKey} onChange={(e) => setEventKey(e.target.value)} data-testid="event-select">
                  <option value="">— イベントを選択 —</option>
                  {info.events.map(ev => (
                    <option key={ev.key} value={ev.key}>{ev.label}</option>
                  ))}
                </select>
              </div>
            )}

            {(uploadType === "player" ? playerId : uploadType === "event" ? eventKey : false) && (
              <>
                <label className="block text-xs uppercase tracking-wider mb-2 mt-6" style={{color: "var(--text-muted)"}}>
                  <FileVideo size={12} className="inline mr-1"/> ファイルを選ぶ
                </label>
                <label className="block panel p-6 text-center cursor-pointer hover:border-[var(--gold)] transition-colors mb-2" data-testid="file-dropzone">
                  <Upload size={20} className="mx-auto mb-2" style={{color: "var(--gold)"}}/>
                  <div className="text-sm mb-1">タップしてファイルを選択</div>
                  <div className="text-[11px]" style={{color: "var(--text-muted)"}}>動画・写真OK／複数選択OK／合計最大 {Math.round(info.max_size_mb / 1024)}GB</div>
                  <div className="text-[10px] mt-1" style={{color: "var(--gold)"}}>📱 ファイル名はそのままでOK（自動でリネームされます）</div>
                  <input
                    type="file"
                    multiple
                    accept="video/*,image/*"
                    className="hidden"
                    onChange={onSelectFiles}
                    data-testid="file-input"
                  />
                </label>
                {files.length > 0 && (
                  <div className="text-xs mb-4" data-testid="file-list">
                    <div className="mb-1" style={{color: "var(--text-secondary)"}}>選択済み: {files.length}件 / {totalSizeMb.toFixed(1)} MB</div>
                    <ul className="space-y-1 max-h-40 overflow-auto">
                      {files.map((f, i) => (
                        <li key={i} className="truncate" style={{color: "var(--text-muted)"}}>• {f.name} ({(f.size / 1024 / 1024).toFixed(1)}MB)</li>
                      ))}
                    </ul>
                    {overSize && <p className="mt-2" style={{color: "var(--red-warn)"}}>※ 合計サイズが上限を超えています</p>}
                  </div>
                )}

                <label className="block text-xs uppercase tracking-wider mb-2 mt-4" style={{color: "var(--text-muted)"}}>投稿者名（任意）</label>
                <input
                  className="cinema mb-3"
                  value={contributorName}
                  onChange={(e) => setContributorName(e.target.value)}
                  placeholder="例: ○○の母"
                  data-testid="contributor-input"
                />

                <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>メッセージ（任意）</label>
                <textarea
                  className="cinema mb-6"
                  rows="3"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="例: 先日の練習試合の様子です"
                  data-testid="message-input"
                />

                <button
                  className="btn-gold w-full"
                  onClick={onSubmit}
                  disabled={!files.length || overSize || (info && info.service_available === false)}
                  data-testid="upload-submit"
                >
                  <Send size={14}/> {info && info.service_available === false ? "現在投稿できません" : `投稿する（${files.length}件）`}
                </button>
              </>
            )}
          </div>
        )}

        {/* Step: アップロード中 */}
        {step === "uploading" && (
          <div className="text-center py-8" data-testid="uploading-step">
            <Upload size={32} className="mx-auto mb-4 animate-pulse" style={{color: "var(--gold)"}}/>
            <p className="text-sm mb-4" style={{color: "var(--text-secondary)"}}>
              アップロード中です...<br/>
              ページを閉じないでください
            </p>
            <div className="h-2 rounded-full overflow-hidden" style={{background: "var(--bg-deep)"}}>
              <div className="h-full transition-all" style={{width: `${progress}%`, background: "var(--gold)"}}/>
            </div>
            <p className="text-xs mt-2" style={{color: "var(--text-muted)"}}>{progress}%</p>
          </div>
        )}

        {/* Step: 完了 */}
        {step === "done" && (
          <div className="text-center py-4" data-testid="done-step">
            <CheckCircle2 size={48} className="mx-auto mb-4" style={{color: "var(--green-success)"}}/>
            <h2 className="font-display text-2xl mb-2">ありがとうございました</h2>
            <p className="text-sm mb-6" style={{color: "var(--text-secondary)"}}>
              {results.length}件アップロード完了{errors.length > 0 ? ` / ${errors.length}件失敗` : ""}
            </p>

            {results.length > 0 && (
              <ul className="text-left text-xs space-y-1 mb-4 max-h-60 overflow-auto" data-testid="result-list">
                {results.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 panel p-2">
                    <CheckCircle2 size={12} style={{color: "var(--green-success)"}} className="mt-0.5 shrink-0"/>
                    <span style={{color: "var(--text-secondary)"}}>{r.name} → {r.destination} ({r.size_mb}MB)</span>
                  </li>
                ))}
              </ul>
            )}
            {errors.length > 0 && (
              <ul className="text-left text-xs space-y-1 mb-4">
                {errors.map((er, i) => (
                  <li key={i} className="flex items-start gap-2 panel p-2" style={{borderColor: "var(--red-warn)"}}>
                    <AlertCircle size={12} style={{color: "var(--red-warn)"}} className="mt-0.5 shrink-0"/>
                    <span style={{color: "var(--text-muted)"}}>{er.name}: {er.error}</span>
                  </li>
                ))}
              </ul>
            )}

            <button className="btn-ghost" onClick={reset} data-testid="upload-more">
              続けて投稿する
            </button>
          </div>
        )}

      </motion.div>
    </div>
  );
};

export default UploadContribute;
